import React, { useState } from 'react';
import { HourglassOutlined, MessageOutlined, RollbackOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { nullSafeGet, stopDefaultEvents } from '../../../utils/DataAccessUtils';
import { getWorkOrderLink } from '../../../utils/WorkOrderUtils';
import { UserNameWithEmail } from '../../name_component/user/UserNameWithEmail';
import RowChatInput from '../../row_chat_input/RowChatInput';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../../thunks/work_orders_thunks';

const moment = require('moment');

interface WorkOrderRowNotePreviewProps {
	workOrder: any;
	userType: string;
	targetCollectionName: string | null;
	refreshWOFromSearch: (id: number) => void;
}

const WorkOrderRowNotePreview: React.FC<WorkOrderRowNotePreviewProps> = ({
	workOrder,
	userType,
	targetCollectionName,
	refreshWOFromSearch,
}) => {
	const location = useLocation();
	const history = useHistory();
	const [replyOpen, setReplyOpen] = useState(false);

	const lastNote = nullSafeGet('lastNote', workOrder);
	const lastNoteUnread = nullSafeGet('isLastNoteUnreadByCurrentUser', workOrder);

	if (!lastNote) {
		return (
			<div className="rowTertiary">
				{!replyOpen && (
					<div className="max-h-0 overflow-hidden opacity-0 transition-all duration-200 ease-out group-hover:max-h-8 group-hover:opacity-100">
						<div className="rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 bg-grey-3 px-2 py-1">
							<span
								role="button"
								className="footnoteregular inline-flex cursor-pointer items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 py-0.5 text-character-primary hover:border-character-primary"
								onClick={(e) => {
									stopDefaultEvents(e);
									setReplyOpen(true);
								}}
							>
								<MessageOutlined className="text-xs" />
								Send Note
							</span>
						</div>
					</div>
				)}
				{replyOpen && (
					<div
						onClick={(e) => stopDefaultEvents(e)}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								setReplyOpen(false);
							}
						}}
						className="w-full"
					>
						<RowChatInput
							workOrder={workOrder}
							autoFocus
							onNoteSent={() => {
								setReplyOpen(false);
								const id = parseInt(nullSafeGet('id', workOrder), 10);
								if (id) refreshWOFromSearch(id);
							}}
							onCancel={() => setReplyOpen(false)}
						/>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="rowTertiary">
			<div
				className={`group flex min-h-8 min-w-0 max-w-full cursor-pointer flex-col items-start gap-1 overflow-hidden rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 px-2 py-1 ${
					replyOpen ? 'w-full bg-grey-4' : 'bg-grey-3 hover:bg-grey-5 hover:shadow-sm'
				} `}
			>
				<div
					onClick={(e) => {
						stopDefaultEvents(e);
						history.push(
							getWorkOrderLink({
								userType,
								tab: 'notes',
								workOrder,
								targetCollectionName,
								location,
								BACK_TEXT: 'Back to Work Orders',
							})
						);
					}}
					className="flex min-w-0 max-w-full cursor-pointer flex-row items-center gap-2"
				>
					<span className="relative flex shrink-0 items-center">
						{nullSafeGet('isLastNoteResponsePending', workOrder) ? (
							<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
								{'[>7d]'}
							</span>
						) : null}
						<span className="relative mr-1 inline-block">
							{nullSafeGet('isLastNoteAwaitingResponseFromCurrentUser', workOrder) ||
							nullSafeGet('isLastNoteAwaitingResponse', workOrder) ? (
								<span
									className={`bodybold flex flex-row items-center gap-1 whitespace-nowrap ${
										nullSafeGet('isLastNoteAwaitingResponseFromCurrentUser', workOrder)
											? 'text-sunsetOrange-8'
											: 'text-character-primary'
									}`}
								>
									<HourglassOutlined className="text-sm" />
								</span>
							) : (
								<MessageOutlined className="text-xs text-grey-9" />
							)}
							{lastNoteUnread && (
								<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
							)}
						</span>

						{nullSafeGet('isLastNoteAwaitingResponseFromCurrentUser', workOrder) ? (
							<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
								Waiting on you:
							</span>
						) : nullSafeGet('isLastNoteAwaitingResponse', workOrder) ? (
							<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
								Awaiting response:
							</span>
						) : null}
					</span>
					<span
						className="footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
						title={nullSafeGet('lastNote', workOrder)}
					>
						{nullSafeGet('lastNote', workOrder)}
					</span>
					<span className="footnoteregular ml-1 shrink-0 text-grey-9">-</span>
					<span className="flex shrink-0 items-center">
						<UserNameWithEmail
							mode="avatar"
							email={nullSafeGet('lastNoteAddedBy', workOrder)}
							size="small"
						/>
					</span>
					<span className="footnoteregular shrink-0 whitespace-nowrap text-grey-9">
						{' ∙ '}
						{moment(nullSafeGet('lastNoteAddedAt', workOrder)).format('hh:mm a')}
					</span>
					{!replyOpen && (
						<span
							role="button"
							className="footnoteregular ml-2 hidden shrink-0 cursor-pointer flex-row items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary group-hover:inline-flex"
							onClick={(e) => {
								stopDefaultEvents(e);
								setReplyOpen(true);
							}}
						>
							<RollbackOutlined className="text-xs" />
							Reply
						</span>
					)}
				</div>
				{replyOpen && (
					<div
						onClick={(e) => stopDefaultEvents(e)}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								setReplyOpen(false);
							}
						}}
						className="w-full"
					>
						<RowChatInput
							workOrder={workOrder}
							autoFocus
							onNoteSent={() => {
								setReplyOpen(false);
								const id = parseInt(nullSafeGet('id', workOrder), 10);
								if (id) refreshWOFromSearch(id);
							}}
							onCancel={() => setReplyOpen(false)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshWOFromSearch: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.read({ id })
				: workOrdersRestCrudThunksForBuyer.read({ id })
		),
});

export default connect(null, mapDispatchToProps)(WorkOrderRowNotePreview as any);
